import {
	ContentWithSidebar,
	IFrame,
	Toolbar,
	TreeView,
	TreeViewItem
} from "client/Components";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	ReactElement,
	Ref,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { createRoot } from "react-dom/client";
import * as windowFactory from "client_legacy/ndaw/windowFactory";
import styles from "./Page.module.less";
import { InstanceOperatorLayout } from "../InstanceOperatorLayout";

export type PageProps = {
	folder: Folder;
};

export const Page = (props: PageProps): ReactElement => {
	const { folder } = props;

	const [activeFilePath, setActiveFilePath] = useState<string | undefined>();
	const iframeRef: Ref<HTMLIFrameElement> = useRef<HTMLIFrameElement>(null);

	const nodes = useMemo(
		(): readonly TreeViewItem<string>[] => folder.items
			.map(convertToTreeViewItem),
		[folder.items]
	);

	const activeFilePathUrl = useMemo(
		(): string | undefined => {
			if (isNil(activeFilePath)) {
				return undefined;
			}

			return "/admin/downloadLogfile?downloadPath=" + activeFilePath;
		},
		[activeFilePath]
	);

	const downloadHandler = useCallback(
		(): void => {
			if (isNil(activeFilePathUrl)) {
				return;
			}

			windowFactory.open(activeFilePathUrl);
		},
		[activeFilePathUrl]
	);

	const refreshHandler = useCallback(
		(): void => {
			const element: HTMLIFrameElement | null = iframeRef.current;
			if (!element) {
				return;
			}

			// eslint-disable-next-line no-self-assign
			element.src = element.src;
		},
		[]
	);

	return (
		<InstanceOperatorLayout>
			<ContentWithSidebar>
				<ContentWithSidebar.Sidebar>
					<TreeView
						value={activeFilePath}
						onChange={setActiveFilePath}
						trackingId="logs-tree"
						items={nodes}
					/>
				</ContentWithSidebar.Sidebar>

				<ContentWithSidebar.Content hasBackgroundColor>
					<div className={styles.logContainer}>
						<div className={styles.iframeWrapper}>
							{activeFilePathUrl && (
								<IFrame
									src={activeFilePathUrl}
									ref={iframeRef}
									trackingId="instance-operator-log-iframe"
									permissions={[]}
								/>
							)}
						</div>
						<div className={styles.buttonsOverlay}>
							{activeFilePath && (
								<Toolbar>
									<Toolbar.Button
										kind="secondary"
										icon="arrow-rotate-right"
										onClick={refreshHandler}
										text=""
									/>

									<Toolbar.Button
										kind="secondary"
										icon="download"
										onClick={downloadHandler}
										text=""
									/>
								</Toolbar>
							)}
						</div>
					</div>
				</ContentWithSidebar.Content>
			</ContentWithSidebar>
		</InstanceOperatorLayout>
	);
};
Page.displayName = "Page";

function convertToTreeViewItem(item: TreeItem): TreeViewItem<string> {
	return {
		value: item.path,
		content: item.name,
		children: isFolder(item)
			? item.items
				.map(i => convertToTreeViewItem(i))
			: undefined,
	};
}

type FileSystemItem = {
	name: string;
	path: string;
	hasItems: boolean;
};

type Folder = FileSystemItem & {
	items: TreeItem[];
};

type TreeItem = FileSystemItem | Folder;

function isFolder(item: TreeItem): item is Folder {
	return isNotNil(
		(item as Folder).items
	);
}

export function init(folder: Folder): void {
	createRoot(
		ensureNotNil(
			document.getElementById("instance-operator-logs")
		)
	)
		.render(
			<Page folder={folder} />
		);
}
